import VideoCard from "../components/common/VideoCard";
import { AlertTriangle } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import useSelectVideo from "../hooks/useSelectVideo";
import { useDispatch, useSelector } from "react-redux";
import { useOutsideClick } from "../hooks/useOutsideClick";
import AccountTab from "../components/account/AccountTab";
import { useCallback, useEffect, useRef, useState } from "react";
import AccountTabModal from "../components/account/AccountTabModal";
import SkeletonVideoCard from "../components/skeleton/SkeletonVideoCard";
import AccountProfileSection from "../components/account/AccountProfileSection";
import { AccountProfile, AccountProfilePostDelete, AccountProfilePosts, resetLoadingMore, UserAccountFollowerlist, UserAccountFollowinglist, removePostFromList } from "../redux/ApiSlice/AccountProfileSlice";
import { fetchVideoStatus, removePendingUpload } from "../redux/ApiSlice/UploadVideoSlice";

export default function AccountPage() {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const ProfileDetails = useSelector((state) => state.AccountProfile);
    const { User_profile, User_posts, loading, loadingMore, nextPage } = ProfileDetails;

    const [show, setShow] = useState(false);
    const [activeTab, setActiveTab] = useState("");
    const [deletePostId, setDeletePostId] = useState(null);
    const ref = useRef(null);
    const postsScrollRef = useRef(null);
    const postsTabScrollRef = useRef(null);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const isLoadingRef = useRef(false);
    const postsRequestedRef = useRef(false);
    const pollingIntervalRef = useRef(null);
    const [showRejectedPopup, setShowRejectedPopup] = useState(false);

    const { pendingUploads } = useSelector((state) => state.Uploadvideo);

    // Ids still awaiting a moderation decision — only these need polling.
    const pollingIdsKey = Object.keys(pendingUploads)
        .filter((id) => pendingUploads[id].status === "pending")
        .join(",");

    // Own posts stay visible immediately after upload even if a list refetch
    // (e.g. the public-posts endpoint) doesn't yet include pending/rejected posts.
    const existingPostIds = new Set((User_posts || []).map((post) => post.id));
    const missingPendingPosts = Object.values(pendingUploads)
        .filter((entry) => !existingPostIds.has(entry.post.id))
        .map((entry) => entry.post);
    const displayPosts = [...missingPendingPosts, ...(User_posts || [])];

    const handleClickOutside = () => {
        setShow(false);
        setActiveTab("");
    };
    useOutsideClick(ref, handleClickOutside);

    const selectVideo = useSelectVideo();

    const handleSelectVideo = async (id) => {
        selectVideo(id, User_posts, location.pathname, 'account', nextPage);
    };

    useEffect(() => {
        // Scroll to top when component mounts (for mobile navigation)
        window.scrollTo({ top: 0, behavior: 'smooth' });
        dispatch(resetLoadingMore());
        dispatch(UserAccountFollowerlist());
        dispatch(UserAccountFollowinglist());
    }, []);

    useEffect(() => {
        if (!User_profile?.id) {
            dispatch(AccountProfile());
        }
    }, [dispatch, User_profile?.id]);

    useEffect(() => {
        if (User_profile?.id) {
            postsRequestedRef.current = true;
            dispatch(AccountProfilePosts({ id: User_profile?.id }));
        }
    }, [dispatch, User_profile?.id]);

    // Reset loading ref when loading completes
    useEffect(() => {
        if (!loadingMore) {
            isLoadingRef.current = false;
        }
    }, [loadingMore]);

    // Poll video moderation status every 4s for every post still pending
    useEffect(() => {
        const ids = pollingIdsKey ? pollingIdsKey.split(",") : [];
        if (ids.length === 0) {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            return;
        }

        const poll = () => {
            ids.forEach((id) => dispatch(fetchVideoStatus(id)));
        };

        poll();
        pollingIntervalRef.current = setInterval(poll, 4000);

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [pollingIdsKey, dispatch]);

    // A rejected video doesn't stay in "My Posts" — pull it out of view and
    // surface the community-guidelines popup instead.
    useEffect(() => {
        const rejectedId = Object.keys(pendingUploads).find(
            (id) => pendingUploads[id].status === "rejected"
        );
        if (!rejectedId) return;

        dispatch(removePendingUpload(rejectedId));
        dispatch(removePostFromList(Number(rejectedId)));
        setShowRejectedPopup(true);
    }, [pendingUploads, dispatch]);

    // Once approved, refetch the public posts list (page 1) so the post's
    // real public data replaces the optimistic entry, then stop tracking it.
    useEffect(() => {
        const approvedId = Object.keys(pendingUploads).find(
            (id) => pendingUploads[id].status === "approved"
        );
        if (!approvedId) return;

        if (User_profile?.id) {
            dispatch(AccountProfilePosts({ id: User_profile.id }));
        }
        dispatch(removePendingUpload(approvedId));
    }, [pendingUploads, dispatch, User_profile?.id]);

    const handleDeleteClick = (id) => {
        setDeletePostId(id);
    };

    const checkAndLoadMoreIfNeeded = useCallback(() => {
        const container = postsScrollRef.current;
        if (!container || !User_profile?.id || !nextPage || loadingMore || isLoadingRef.current) {
            return;
        }

        // Check if content is shorter than container height (no scroll possible)
        const needsMoreContent = container.scrollHeight <= container.clientHeight;

        if (needsMoreContent) {
            // Prevent duplicate calls
            isLoadingRef.current = true;

            // Automatically load next page
            dispatch(
                AccountProfilePosts({
                    id: User_profile?.id,
                    nextUrl: nextPage,
                    isLoadMore: true,
                })
            );

            // Reset after a delay
            setTimeout(() => {
                isLoadingRef.current = false;
            }, 1000);
        }
    }, [dispatch, loadingMore, nextPage, User_profile?.id]);

    const handleConfirmDelete = async () => {
        if (deletePostId) {
            await dispatch(AccountProfilePostDelete(deletePostId));
            dispatch(removePendingUpload(deletePostId));
            setDeletePostId(null);

            // Check if we need to load more content after deletion
            setTimeout(() => {
                checkAndLoadMoreIfNeeded();
            }, 100);
        }
    };

    const handleCancelDelete = () => {
        setDeletePostId(null);
    };
    const handleCloseProfileModal = () => {
        setShowProfileModal(false);
    }

    const handlePostsScroll = useCallback(() => {
        const container = postsScrollRef.current;
        if (!container || !User_profile?.id || !nextPage || loadingMore || isLoadingRef.current) {
            return;
        }

        const hasReachedBottom =
            container.scrollTop + container.clientHeight >= container.scrollHeight - 10;


        if (hasReachedBottom) {
            // Prevent duplicate calls
            isLoadingRef.current = true;

            dispatch(
                AccountProfilePosts({
                    id: User_profile?.id,
                    nextUrl: nextPage,
                    isLoadMore: true,
                })
            );

            // Reset after a delay
            setTimeout(() => {
                isLoadingRef.current = false;
            }, 1000);
        }
    }, [dispatch, loadingMore, nextPage, User_profile?.id]);

    useEffect(() => {
        const container = postsScrollRef.current;
        if (!container) return;

        container.addEventListener("scroll", handlePostsScroll);

        return () => {
            container.removeEventListener("scroll", handlePostsScroll);
        };
    }, [handlePostsScroll]);

    return (
        <div className="h-full relative">
            <div
                ref={postsScrollRef}
                className="h-full bg-white dark:bg-[#1E1D21] dark:text-white overflow-y-scroll overflow-hidden relative"
            >
                <AccountProfileSection
                    show={show}
                    setShow={setShow}
                    setActiveTab={setActiveTab}
                    setShowProfileModal={setShowProfileModal}
                />

                <AccountTab activeTab={"Posts"} />

                <div className="mb-0">
                    {loading ? (
                        <SkeletonVideoCard />
                    ) : (
                        displayPosts.length > 0 && (
                            <div className="grid grid-cols-3 gap-1 px-2 pb-2 w-full h-full">
                                {displayPosts.map((item) => {
                                    const pending = pendingUploads[item.id];
                                    return (
                                        <div key={item.id} className="relative">
                                            <VideoCard
                                                id={item.id}
                                                hideSound={false}
                                                body={item.body}
                                                belowDecs={true}
                                                threeDots={false}
                                                video={item.video_url}
                                                likes_count={item.likes_count}
                                                view_count={item.view_count}
                                                authorId={item.created_by?.id}
                                                selectVideo={pending ? undefined : handleSelectVideo}
                                                onDeleteClick={handleDeleteClick}
                                                avatar_url={item.created_by?.avatar_url}
                                                showDelete={!pending}
                                                thumbnail={item.thumbnail_url}
                                            />
                                            {pending?.status === "pending" && (
                                                <div className="absolute inset-0 rounded-md z-10 flex flex-col justify-between p-1">
                                                    <span className="self-start flex items-center gap-1 bg-yellow-400 text-black text-[8px] font-bold px-1.5 py-0.5 rounded-full shadow">
                                                        <span className="w-2 h-2 border-[1.5px] border-black border-t-transparent rounded-full animate-spin" />
                                                        Under Review
                                                    </span>
                                                    <p className="bg-black/60 text-white text-[8px] text-center leading-tight rounded px-1 py-0.5">
                                                        Only visible to you until approved.
                                                    </p>
                                                </div>
                                            )}
                                            {pending?.status === "pipeline_failed" && (
                                                <div className="absolute inset-0 bg-black/65 flex flex-col items-center justify-center rounded-md z-10 p-1">
                                                    <p className="text-white text-[9px] text-center leading-tight mb-1">Something went wrong with the video. Our team is reviewing your video.</p>
                                                    <button
                                                        onClick={() => dispatch(removePendingUpload(item.id))}
                                                        className="text-[8px] bg-white/20 text-white px-2 py-0.5 rounded-full"
                                                    >
                                                        Dismiss
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    )}
                    {loadingMore && (
                        <div className="flex justify-center items-center py-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                        </div>
                    )}
                </div>

                {displayPosts.length === 0 && !loading && postsRequestedRef.current && (
                    <div className="flex justify-center items-start w-full">
                        <p className="text-gray-600 text-[12px] mt-24">
                            Your posts will appear here.
                        </p>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal - Positioned outside scrollable container */}
            {deletePostId && (
                <div
                    className="absolute top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]"
                    onClick={handleCancelDelete}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm mx-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p className="text-[24px] font-bold text-black dark:text-white text-center">
                            Delete Post?
                        </p>
                        <p className="text-[12px] text-black mb-2 dark:text-white text-center">
                            Are you sure you want to delete this post?
                        </p>
                        <div className="flex gap-3 mt-4 justify-center">
                            <button
                                onClick={handleCancelDelete}
                                className="px-2 py-1 rounded-md bg-[#1b1b1b] dark:bg-gray-400 dark:text-black dark:hover:bg-gray-500 hover:bg-[#0f0f0f] text-white"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                className="px-2 py-1 rounded-md border border-gray-400 dark:border-gray-800 bg-red-900 hover:bg-red-600 text-white"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showRejectedPopup && (
                <div className="absolute top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
                    <div className="bg-white w-[80%] max-w-sm dark:bg-gray-900 px-6 py-4 rounded-xl shadow-lg text-center">
                        <div className="mb-3">
                            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <AlertTriangle className="w-6 h-6 text-red-600" />
                            </div>
                        </div>
                        <p className="text-[15px] font-bold mb-2 text-gray-900 dark:text-white">
                            Your video has violated our community guidelines.
                        </p>
                        <p className="mb-6 text-[13px] text-gray-600 dark:text-gray-300">
                            Please check "Your Violations" in the Report Center for more information.
                        </p>
                        <div className="flex flex-col gap-3">
                            <button
                                className="flex-1 text-[14px] bg-[#BDE6FF] hover:bg-[#9dd5f0] text-black font-semibold px-6 py-2 rounded-full transition-colors"
                                onClick={() => {
                                    setShowRejectedPopup(false);
                                    navigate("/report-center", { state: { initialTab: "violations" } });
                                }}
                            >
                                Go to Report Center
                            </button>
                            <button
                                className="flex-1 text-[14px] bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold px-6 py-2 rounded-full transition-colors"
                                onClick={() => setShowRejectedPopup(false)}
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {show && (
                <div className={"absolute h-full w-full top-0 overflow-hidden"}>
                    <div
                        className="absolute h-screen w-full top-0 opacity-100 bg-opacity-70 bg-black z-50"
                        onClick={handleClickOutside}
                    />
                    <div
                        ref={ref}
                        className="absolute top-0 left-2 z-50 w-[95%] h-[-webkit-fill-available] my-10 rounded-lg bg-black duration-1000 transform opacity-100"
                    >
                        <AccountTabModal
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            postsTabScrollRef={postsTabScrollRef}
                        />
                    </div>
                </div>
            )}

            {showProfileModal && (
                <div
                    className="fixed inset-0 z-[220] flex items-end bg-black/40"
                    onClick={handleCloseProfileModal}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full bg-white dark:bg-gray-900 rounded-t-2xl shadow-md px-4 pt-2 pb-5"
                    >
                        <div className="flex justify-center py-1">
                            <div className="w-16 rounded-full h-[3px] bg-gray-700 dark:bg-gray-500" />
                        </div>

                        <div className="w-full flex justify-center pt-2">
                            <img
                                src={User_profile?.avatar}
                                alt={`${User_profile?.full_name}'s profile`}
                                className="w-full max-w-md max-h-[58vh] object-contain rounded-lg"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
